import { InjectionToken } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { WorkflowModel } from '@models/workflow.model';

export interface FlowNodePort {
  id: string;
  name?: string;
}

export interface FlowNode {
  id: string;
  name?: string;
  type?: string;
  icon?: string;
  position: Position;
  nodeType: FlowNodeTypes.Trigger | FlowNodeTypes.Action;
  inputs: FlowNodePort[];
  outputs: FlowNodePort[];
  data?: {
    url?: string;
    method?: string;
    wasInited?: boolean;
  } & Record<string, unknown>;
  inputData?: unknown;
}

export interface FlowSticker {
  id: string;
  position: Position;
  nodeType: FlowNodeTypes.Sticker;
  text: string;
  color: string;
}

export type FlowNodeOrSticker = FlowNode | FlowSticker;

export interface FlowConnection {
  from: string;
  to: string;
  id: string;
}

export interface FlowNodeConfig {
  name: string;
  description?: string;
  type: WorkflowModel.WorkflowNodeTypes;
  properties?: Record<string, unknown>;
  nodeType: FlowNodeTypes;
  icon: string;
  inputNames?: string[];
  outputNames?: string[];
}

export enum FlowNodeTypes {
  Trigger = 'trigger',
  Action = 'action',
  Sticker = 'sticker',
}

export interface FlowTrigger extends FlowNodeConfig {
  inputData?: unknown;
}

export interface FlowAction extends FlowNodeConfig {
  actionGroup: WorkflowActionGroups;
}

export interface FlowState {
  flowNodes: {
    nodes: FlowNode[],
    stickers: FlowSticker[],
  };
  connections: FlowConnection[];
}

export interface FlowConfig {
  triggers: FlowTrigger[];
  actions: FlowAction[];
}

export enum SideNavModes {
  TriggerMenu = 'triggerMenu',
  ActionMenu = 'actionMenu',
}

export enum GraphModes {
  Preview = 'preview',
  Publish = 'publish',
}

export enum WorkflowActionGroups {
  Notifications = 'notifications',
  Flow = 'flow',
  Actions = 'actions',
  Wait = 'wait',
  Video_Processing = 'video_processing',
  AI = 'ai',
}

export const WorkflowActionGroupsMap = {
  [WorkflowActionGroups.Notifications]: 'FLOW_CHART.ACTION_GROUPS.NOTIFICATION',
  [WorkflowActionGroups.Flow]: 'FLOW_CHART.ACTION_GROUPS.FLOW',
  [WorkflowActionGroups.Actions]: 'FLOW_CHART.ACTION_GROUPS.ACTIONS',
  [WorkflowActionGroups.Wait]: 'FLOW_CHART.ACTION_GROUPS.WAIT',
  [WorkflowActionGroups.Video_Processing]: 'FLOW_CHART.ACTION_GROUPS.VIDEO_PROCESSING',
  [WorkflowActionGroups.AI]: 'FLOW_CHART.ACTION_GROUPS.AI',
}

export interface Position {
  x: number;
  y: number;
}

export const LINE_WIDTH = 100;
export const NODE_WIDTH = 104;
export const SPACE_BETWEEN_CONDITIONS = 80;
export const START_NODE_ID = 'START_NODE_ID';

export interface SelectedNodeForEdit<TData> {
  id: string;
  type: string;
  name: string;
  nodeType: FlowNodeTypes;
  data: TData & DialogOptions;
}

export interface DialogOptions {
  shouldMarkFormAsTouched?: boolean;
  disableForm?: boolean;
  previousNodes: PreviousNode[];
}

export interface PreviousNode {
  id: string;
  icon: string;
  name: string;
}

export interface NodeEditDialogOutput {
  id: string;
  data: Record<string, unknown>;
}

export const NODE_EDIT_TAB_COMPONENT = new InjectionToken<NodeEditTab>(
  'NODE_EDIT_TAB_COMPONENT',
);

export interface NodeEditTab {
  form?: UntypedFormGroup;
}

export interface DataForm {
  name: FormControl<string>;
  tags: FormControl<string[] | null>;
}

export interface WorkflowExport {
  flowNodes: {
    nodes: FlowNode[];
    stickers: FlowSticker[];
  };
  connections: FlowConnection[];
  name: string;
  tags: string[];
}

export interface WorkflowConnectionExport {
  kind: 'main';
  type: string;
  fromNode: string;
  fromPort: number;
  toNode: string;
  toPort: number;
}

export interface WorkflowTaskExport {
  nodeType: string;
  nodeId: string;
  input: {
    main: unknown[];
  };
}

export interface UiNodeData {
  position: Position;
  inputs?: FlowNodePort[];
  outputs?: FlowNodePort[];
  nodeType: FlowNodeTypes.Trigger | FlowNodeTypes.Action;
}

export interface PortRef {
  node: FlowNode;
  portIndex: number;
  direction: 'input' | 'output';
}

